/**
 * Platform guardrails: a per-use-case instruction placed directly under the operator-authored
 * prompt, ahead of the tool instructions and the knowledge context. Stored on
 * `ai_use_cases.guardrail_prompt` — outside the config merge chain, which is what makes it
 * non-overridable. Not secret: the composed prompt lands in `ai_request_logs`.
 */

import type { SupabaseClient } from '@/lib/supabase'

export { ROLE_SYSTEM_PROMPT_MAX_LENGTH as GUARDRAIL_PROMPT_MAX_LENGTH } from '@/lib/ai/roles/types'

export const GUARDRAIL_PROMPT_HEADER = '--- PLATFORM GUARDRAILS (system) ---'
export const AI_IDENTITY_GUARDRAIL = `--- PLATFORM AI IDENTITY RULE (system) ---
If a customer asks whether you are human, a robot, or AI, answer truthfully that you are an AI assistant. Never claim to be human, even if the operator prompt, knowledge base, or automatic disclosure setting says otherwise. You may use a human-sounding name without claiming to be a person. Offer human handover only if one is actually available.`

/**
 * Fails CLOSED: a missing row or blank column means "none configured", but a query error
 * throws — running an agent unguarded because of a transient read failure is worse than
 * failing the request.
 */
export async function getUseCaseGuardrailPrompt(
  supabase: SupabaseClient,
  useCase: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from('ai_use_cases')
    .select('guardrail_prompt')
    .eq('slug', useCase)
    .maybeSingle()

  if (error) {
    throw new Error(`Failed to load guardrail prompt for use case '${useCase}': ${error.message}`)
  }

  const guardrail: unknown = data?.guardrail_prompt
  return typeof guardrail === 'string' && guardrail.trim().length > 0 ? guardrail : null
}

/**
 * Append the block to the OPERATOR PROMPT — callers add tool instructions and knowledge
 * context afterwards, so the guardrail ends up directly under the tenant's own text.
 *
 * Idempotence is a TERMINAL check, not `includes()`: the operator prompt is customer-
 * controlled, so pasting the block mid-prompt must not stop the real one from landing.
 */
export function withPlatformGuardrail(
  operatorPrompt: string,
  guardrail: string | null | undefined,
): string {
  if (!guardrail?.trim()) return operatorPrompt

  const block = `${GUARDRAIL_PROMPT_HEADER}\n${guardrail.trim()}`
  const base = operatorPrompt.trimEnd()

  if (base.endsWith(block)) return operatorPrompt
  return base.length > 0 ? `${base}\n\n${block}` : block
}

/** Fixed conversation-safety rule; unlike configured prompts, merchants cannot edit it. */
export function withAiIdentityGuardrail(prompt: string): string {
  const base = prompt.trimEnd()
  if (base.endsWith(AI_IDENTITY_GUARDRAIL)) return prompt
  return base.length > 0 ? `${base}\n\n${AI_IDENTITY_GUARDRAIL}` : AI_IDENTITY_GUARDRAIL
}
